import type { FloorPlanLayoutView } from "components/SmartSurveyor/SmartSurveyorV3/utils/types";
import type { LayoutLabelStyle, LayoutViewStyle } from "./types";

export const LAYOUT_STYLE_BY_VIEW: Record<
  FloorPlanLayoutView,
  LayoutViewStyle
> = {
  // Solid colour and a wall-wide stroke, faded as one, so a zone reads as one block.
  zones: {
    fillOpacity: 1,
    opacity: 0.35,
    screenStrokeWidth: null,
    isStrokeDarker: false,
    borderScreenWidth: 2,
    hoverClassName: "hover:[opacity:0.55]",
    highlightFillOpacity: 1,
    highlightOpacity: 0.55,
    // The other zones turn slate grey, cooler than the grey of rooms without a zone so the two stay apart.
    greyOutSiblingsClassName: [
      "[&:is(:hover,[data-highlighting])>g:not(:hover,[data-highlighted])>[data-layout-part=fill]]:[opacity:0.2]",
      "[&:is(:hover,[data-highlighting])>g:not(:hover,[data-highlighted])>[data-layout-part=fill]]:[fill:#94a3b8]",
      "[&:is(:hover,[data-highlighting])>g:not(:hover,[data-highlighted])>[data-layout-part=fill]]:[stroke:#94a3b8]",
      "[&:is(:hover,[data-highlighting])>g:not(:hover,[data-highlighted])>[data-layout-part=border]]:[stroke:#64748b]",
      "[&:is(:hover,[data-highlighting])>g:not(:hover,[data-highlighted])>[data-layout-part=border]]:[stroke-opacity:0.5]",
    ].join(" "),
  },
  rooms: {
    fillOpacity: 0.3,
    opacity: 1,
    screenStrokeWidth: 1.4,
    isStrokeDarker: true,
    borderScreenWidth: null,
    hoverClassName: "hover:[fill-opacity:0.5]",
    highlightFillOpacity: 0.5,
    highlightOpacity: 1,
    // The same slate greys as the zones; a room's outline is its own stroke, so there is no border part.
    greyOutSiblingsClassName: [
      "[&:is(:hover,[data-highlighting])>g:not(:hover,[data-highlighted])>[data-layout-part=fill]]:[fill:#94a3b8]",
      "[&:is(:hover,[data-highlighting])>g:not(:hover,[data-highlighted])>[data-layout-part=fill]]:[fill-opacity:0.2]",
      "[&:is(:hover,[data-highlighting])>g:not(:hover,[data-highlighted])>[data-layout-part=fill]]:[stroke:#64748b]",
      "[&:is(:hover,[data-highlighting])>g:not(:hover,[data-highlighted])>[data-layout-part=fill]]:[stroke-opacity:0.5]",
    ].join(" "),
  },
  walls: {
    fillOpacity: 0.6,
    opacity: 1,
    screenStrokeWidth: 1,
    isStrokeDarker: false,
    borderScreenWidth: null,
    hoverClassName: "hover:[fill-opacity:0.9]",
    highlightFillOpacity: 0.9,
    highlightOpacity: 1,
    greyOutSiblingsClassName: "",
  },
};

// Label text keeps this size on screen whatever the zoom (the agent viewer's sizes).
export const LAYOUT_LABEL_STYLE_BY_VIEW: Record<
  FloorPlanLayoutView,
  LayoutLabelStyle
> = {
  zones: { fontScreenPx: 13, fontWeight: "600" },
  rooms: { fontScreenPx: 11, fontWeight: "500" },
  walls: { fontScreenPx: 11, fontWeight: "500" },
};

export const LAYOUT_LABEL_FONT_FAMILY = "Arial, Helvetica, sans-serif";
// Badge padding and corner radius as fractions of the font size.
export const LAYOUT_LABEL_PADDING_X_FACTOR = 0.4;
export const LAYOUT_LABEL_PADDING_Y_FACTOR = 0.2;
export const LAYOUT_LABEL_CORNER_RADIUS_FACTOR = 0.25;
export const LAYOUT_LABEL_BACKGROUND = "rgba(255, 255, 255, 0.88)";
export const LAYOUT_LABEL_OUTLINE_WIDTH = 0.5;
export const LAYOUT_LABEL_OUTLINE_OPACITY = 0.5;

// How close to the deepest point of the shape the anchor search gets, in plan units; a badge needs no better.
export const LABEL_ANCHOR_PRECISION_PT = 5;
// A label is drawn only when its shape is this many times wider and taller than the badge on screen.
export const LABEL_FIT_WIDTH_FACTOR = 1.1;
export const LABEL_FIT_HEIGHT_FACTOR = 1.6;
// Screen pixels kept clear between two badges.
export const LABEL_GAP_PX = 3;

// Offset of the hover card from the point where the cursor entered the shape.
export const TOOLTIP_OFFSET_PX = 14;
// Room kept free at the right and bottom of the viewport before the card flips to the other side.
export const TOOLTIP_FLIP_WIDTH_PX = 340;
export const TOOLTIP_FLIP_HEIGHT_PX = 220;
